import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { ICustomerModel } from "@models/customer/ICustomerModel";
import { CAMPFIRE_PLAN_TYPE } from "@models/data/campfirePlanType";
import {
  CHANGE_SHARING_EVENT,
  buildChangeSharingPayload,
  buildEvictUserPayload,
} from "@models/data/changeSharingWire";
import type { ProjectInfo } from "@models/data/projectInfo";
import {
  ACCOUNT_ROLE,
  DEEPLINK_TYPE,
  INTERNAL_SHARE_EMAIL,
  SHARED_LINK_STATE,
  SHARED_USER_ROLE,
  isRosterShareLink,
  isSharedLinkVisited,
  sharedLinkState,
  sharedLinkUserRole,
  type SharedLinkDto,
} from "@models/data/sharedLinkDto";
import { generateSharedLinkId } from "@models/data/sharedLinkId";
import {
  accountRoleToSharedUserRole,
  canShareProject,
  isProjectOwner,
  type SharedUserRole,
} from "@models/data/userRolePermissions";
import type { IEventModel } from "@models/events/IEventModel";
import { getAppInstanceId } from "@models/multiuser/appInstanceId";
import type { IMultiuserSessionModel } from "@models/multiuser/IMultiuserSessionModel";
import type { IToastsModel } from "@models/notifications/IToastsModel";
import type { IPermissionsModel } from "@models/permissions/IPermissionsModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { HttpAdminStorageModel, ShareeInfo } from "@models/storage/HttpAdminStorageModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type {
  ISharedUsersModel,
  InviteResult,
  SharedUserJoinedInfo,
} from "./ISharedUsersModel";

/**
 * `SharedUsersModel` — port of Unity's `Models/SharedUsersModel.cs`
 * (`SharedUsersBaseModel`) plus the sharing legs that upstream scatters over
 * `ShareViewModel` (invite / revoke flow), `DeepLinkGenerator` (row creation,
 * the share URL, the invite email) and `CustomerModel` (`MarkProjectAsVisited`).
 * They are one flow with one set of rules, so they live in one headless
 * model here; `useSharePanel` is the thin reactive binding over it.
 *
 * ⚠ THINGS THAT LOOK WRONG AND ARE UNITY'S, kept verbatim:
 *  - An email invite writes `LinkType = SceneLink` (3) with an EMPTY
 *    `SceneID`, not `ProjectLink`. Copy Link is the `ProjectLink`.
 *  - The invite row's `CustomerID` is the INVITER's account
 *    (`CurrentUser.CustomerID`), not the project's. They coincide for every
 *    invite the grid allows (same-account only).
 *  - The "cannot share with this user" copy is chosen by
 *    `CanInviteExternalUsers()` the way `ShareViewModel.cs:474-485` chooses
 *    it, which reads inverted; the strings are the desktop's.
 *  - The `lookup` failing is NOT an error path: `GetUserInfo` turns it into
 *    "no such account" (an empty `ShareeInfoModel`), and an exception
 *    anywhere else in the invite still SENDS the invite with no user id.
 *  - `CheckMailValidity`'s owner check compares against `Project.OwnerEmail`,
 *    which for a same-account project is empty until the first invite has
 *    resolved it — so the very first invite of the owner's own address is
 *    caught by the `lookup`, not by that check.
 *
 * DEVIATIONS, each recorded at its site: the web has no hybrid-cloud external
 * credentials, so `canInviteExternalUsers()` is always true and the
 * `UsingExternalCredentials` term of `canShareWithAccount()` is always false;
 * a failed invite EMAIL reports `Error sending invite to …` where Unity
 * fires-and-forgets and toasts success regardless.
 */
export class SharedUsersModel implements ISharedUsersModel {
  readonly onSharingChanged = new Emitter<{ projectId: string }>();

  private readonly m_auth: IAuthModel;
  private readonly m_project: IProjectModel;
  private readonly m_storage: IStorageModel;
  private readonly m_admin: HttpAdminStorageModel;
  private readonly m_customer: ICustomerModel;
  private readonly m_permissions: IPermissionsModel;
  private readonly m_multiuser: IMultiuserSessionModel;
  private readonly m_toasts: IToastsModel;
  private readonly m_events: IEventModel;
  /**
   * Browser deployment base (`https://host/` or
   * `https://host/<channel>/`), injected by the composition root so this
   * headless model never reads `window.location`.
   */
  private readonly m_shareBaseUrl: string;

  /**
   * `SharedUsersBaseModel.m_userShareInfo` — identity per email. Unity caches
   * ONLY users that resolved to an account (`UserId != null`), so a pending
   * invitee is re-queried on every reload and the row flips the moment they
   * sign up. Cleared on sign-out.
   */
  private readonly m_shareeInfoByEmail = new Map<string, ShareeInfo>();
  /** `Project.OwnerEmail`, resolved lazily per project (`ShareViewModel.cs:465-471`). */
  private readonly m_ownerEmailByProjectId = new Map<string, string>();

  constructor(deps: {
    auth: IAuthModel;
    project: IProjectModel;
    storage: IStorageModel;
    admin: HttpAdminStorageModel;
    customer: ICustomerModel;
    permissions: IPermissionsModel;
    multiuser: IMultiuserSessionModel;
    toasts: IToastsModel;
    events: IEventModel;
    shareBaseUrl: string;
  }) {
    this.m_auth = deps.auth;
    this.m_project = deps.project;
    this.m_storage = deps.storage;
    this.m_admin = deps.admin;
    this.m_customer = deps.customer;
    this.m_permissions = deps.permissions;
    this.m_multiuser = deps.multiuser;
    this.m_toasts = deps.toasts;
    this.m_events = deps.events;
    this.m_shareBaseUrl = ensureTrailingSlash(deps.shareBaseUrl);

    // `CustomerModel.LoadCurrentProjectAssets` → `MarkProjectAsVisited`: the
    // first open of a project shared to you flips your link to Visited, which
    // is what lets the owner's roster show your name instead of your email.
    this.m_project.onActiveProjectChanged.on((project) => {
      if (!project) return;
      void this.markProjectAsVisitedAsync(project).catch((err) => {
        console.warn("[SharedUsersModel] mark-visited failed:", err);
      });
    });
    this.m_auth.onSignedOut.on(() => {
      this.m_shareeInfoByEmail.clear();
      this.m_ownerEmailByProjectId.clear();
    });
    // `ShareViewModel.OnPermissionsChanged` re-raises ShowInviteUI /
    // ShowAccountSharingUI and re-reads the account role. Customer data owns
    // the enterprise + share-rule terms, so either model changing announces
    // the same active share subject.
    this.m_permissions.onPermissionsChanged.on(() => this.notifyActiveProjectChanged());
    this.m_customer.onChanged.on(() => this.notifyActiveProjectChanged());
  }

  notifySharingChanged(projectId: string): void {
    if (projectId) this.onSharingChanged.emit({ projectId });
  }

  private notifyActiveProjectChanged(): void {
    const projectId = this.m_project.activeProject?.id;
    if (projectId) this.notifySharingChanged(projectId);
  }

  // -- Gating --------------------------------------------------------------

  canInviteUsers(project: ProjectInfo): boolean {
    return canShareProject(this.activeUserRoleFor(project));
  }

  canEditUsers(project: ProjectInfo): boolean {
    return canShareProject(this.activeUserRoleFor(project));
  }

  canShareWithAccount(project: ProjectInfo): boolean {
    // `m_authModel.UsingExternalCredentials` — hybrid-cloud external project
    // credentials. The web port has no such mode, so the term is false.
    if (!this.canEditUsers(project)) return false;
    const plan = this.m_customer.customerInfo?.planType;
    return plan === CAMPFIRE_PLAN_TYPE.EnterpriseFree || plan === CAMPFIRE_PLAN_TYPE.EnterprisePaid;
  }

  canInviteExternalUsers(): boolean {
    // `!UseExternalIdentity || HybridCloudBucketName == AwsProjectBucketName`
    // — the web models no external identity, so the first disjunct holds.
    return true;
  }

  shareRuleMessage(project: ProjectInfo): string {
    // `UpdateShareRuleText`: the SSO-hybrid branch ("Only members of {account}
    // can access this project.") needs external identity, which the web has
    // not got; the enterprise branch is reachable.
    void project;
    const plan = this.m_customer.customerInfo?.planType;
    if (plan === CAMPFIRE_PLAN_TYPE.EnterpriseFree || plan === CAMPFIRE_PLAN_TYPE.EnterprisePaid) {
      return "To share with external users, your admin must first add them as guests.";
    }
    return "";
  }

  /**
   * `ActiveUserRole` for the project being shared. The OPEN project has a
   * resolved, `maxperms`-clamped role on the permissions model; a grid card
   * does not, so it is derived the way `PermissionsModel.resolveCurrentUserRole`
   * derives it: owner → Owner, an individual link → its role, else the
   * account grant mapped across.
   */
  private activeUserRoleFor(project: ProjectInfo): SharedUserRole {
    const userId = this.m_auth.currentUser?.userId ?? null;
    if (isProjectOwner(project, userId)) return SHARED_USER_ROLE.Owner;
    if (this.m_project.activeProject?.id === project.id) return this.m_permissions.activeUserRole;
    if (typeof project.sharedUserRole === "number") {
      return project.sharedUserRole === SHARED_USER_ROLE.Presenter
        ? SHARED_USER_ROLE.Commenter
        : project.sharedUserRole;
    }
    return accountRoleToSharedUserRole(this.m_permissions.naturalAccountRole);
  }

  // -- Reads ---------------------------------------------------------------

  projectCustomerId(project: ProjectInfo): string {
    return project.customerId || this.m_auth.currentUser?.customerId || "";
  }

  async getProjectLinksAsync(
    project: ProjectInfo,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedLinkDto>> {
    const customerId = this.projectCustomerId(project);
    if (!customerId || !this.m_storage.isOnline) return [];
    return this.m_storage.getSharedLinksByProject(customerId, project.id, signal);
  }

  async getSharedUsersAsync(
    project: ProjectInfo,
    links: ReadonlyArray<SharedLinkDto>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedUserJoinedInfo>> {
    const user = this.m_auth.currentUser;
    const myEmail = (user?.email ?? "").toLowerCase();
    const canEdit = this.canEditUsers(project);
    const rows: SharedUserJoinedInfo[] = [];
    let addedSelf = false;

    for (const link of links) {
      if (!isRosterShareLink(link)) continue;
      const row = await this.createJoinedInfoAsync(project, link, canEdit, signal);
      if (signal?.aborted) return [];
      rows.push(row);
      if (row.email === myEmail) addedSelf = true;
    }

    rows.unshift(await this.createOwnerJoinedInfoAsync(project, signal));
    // "show current user in share view when they are from the same account"
    if (this.activeUserRoleFor(project) !== SHARED_USER_ROLE.Owner && !addedSelf) {
      rows.push(this.createSelfJoinedInfo(project));
    }
    return rows;
  }

  projectLinkUrl(project: ProjectInfo, link: SharedLinkDto): string {
    // Same query contract as `DeepLinkGenerator.URIForLink`, on the WEB
    // deployment rather than Unity's `app[-dev].campfire3d.com/query`
    // launcher. `shareBaseUrl` includes a named deploy channel when present.
    const act = link.CustomerID || this.projectCustomerId(project);
    const url = new URL("query", this.m_shareBaseUrl);
    url.searchParams.set("sl", link.ID);
    url.searchParams.set("act", act);
    url.searchParams.set("prj", project.id);
    return url.toString();
  }

  // -- Writes --------------------------------------------------------------

  async inviteAsync(
    project: ProjectInfo,
    emailInput: string,
    role: SharedUserRole,
    existingLinks: ReadonlyArray<SharedLinkDto>,
  ): Promise<InviteResult> {
    // `m_email` is lowercased by its setter upstream; the split / trim /
    // drop-empties is `InviteUser()` verbatim.
    const emails = emailInput
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const isSingleEmail = emails.length === 1;
    const invited: string[] = [];
    const failed: string[] = [];
    let message: string | null = null;

    for (const email of emails) {
      const invalid = await this.checkMailValidityAsync(project, email, existingLinks);
      if (invalid) {
        failed.push(email);
        // `CheckMailValidity(email, showMessage: isSingleEmail)`.
        if (isSingleEmail) message = invalid;
        debugSharing("invite: refused by CheckMailValidity", { email, reason: invalid });
        continue;
      }
      const result = await this.inviteOneAsync(project, email, role);
      debugSharing("invite: result", { email, ...result });
      if (result.message) message = result.message;
      if (result.ok) invited.push(email);
      else failed.push(email);
    }

    if (failed.length < emails.length) {
      // "invalidate cache files of shared links" — the project timestamp is
      // the desktop's share-list cache key.
      await this.bumpProjectModifiedAsync(project);
      this.onSharingChanged.emit({ projectId: project.id });
    }
    if (!isSingleEmail && failed.length > 0) {
      message = `Failed to invite ${failed.join(", ")}.`;
    }
    return { invited, failed, message };
  }

  async createInternalProjectLinkAsync(
    project: ProjectInfo,
    existingLinks: ReadonlyArray<SharedLinkDto>,
  ): Promise<SharedLinkDto | null> {
    const customerId = this.projectCustomerId(project);
    if (!customerId || !this.m_storage.isOnline) return null;
    const existing = existingLinks.find((link) => link.SharedWithEmail === INTERNAL_SHARE_EMAIL);
    if (existing) return existing;
    // `CreateInternalProjectLink`: ProjectLink, sentinel email, Viewer, and
    // the id is `GenerateID(projectId, "internal")` — so a second Copy Link
    // (or the desktop's) lands on the SAME row instead of adding one.
    const link: SharedLinkDto = {
      ID: generateSharedLinkId(project.id, INTERNAL_SHARE_EMAIL),
      LinkType: DEEPLINK_TYPE.ProjectLink,
      CustomerID: customerId,
      ProjectID: project.id,
      SceneID: "",
      AnnotationID: "",
      SharedWithEmail: INTERNAL_SHARE_EMAIL,
      ProjectAuthor: this.projectAuthorFor(project),
      UserRole: SHARED_USER_ROLE.Viewer,
      State: SHARED_LINK_STATE.Default,
    };
    await this.m_storage.createSharedLink(customerId, link);
    return link;
  }

  async setAccountRoleAsync(project: ProjectInfo, accountRole: SharedUserRole): Promise<void> {
    const customerId = this.projectCustomerId(project);
    if (!customerId || !this.canEditUsers(project)) return;
    await this.m_storage.updateCustomerProjectAccountRole(customerId, project.id, accountRole);
    // `ShareViewModel.SendAccountRoleUpdate`: addressed to the CURRENT USER's
    // account (`CustomerInfo.ID`), `userId = ""` — it is about the account,
    // not a person, which is why receivers handle it in its own branch.
    this.sendChangeSharing({
      accountId: this.m_auth.currentUser?.customerId ?? customerId,
      projectId: project.id,
      userId: "",
      shareEvent: CHANGE_SHARING_EVENT.AccountRoleChanged,
      content: accountRole,
    });
    this.onSharingChanged.emit({ projectId: project.id });
  }

  async updateSharedLinkUserRoleAsync(
    project: ProjectInfo,
    link: SharedLinkDto,
    role: SharedUserRole,
  ): Promise<void> {
    const customerId = link.CustomerID || this.projectCustomerId(project);
    if (!customerId || !this.canEditUsers(project)) return;
    await this.m_storage.updateSharedLinkUserRole(customerId, link.ID, role);
    await this.bumpProjectModifiedAsync(project);
    // `ShareViewModel.UpdateSharedLink`: "use owner's account id, because this
    // event is only necessary for currently active users in the session".
    const sharee = await this.getUserInfoAsync((link.SharedWithEmail ?? "").toLowerCase());
    this.sendChangeSharing({
      accountId: customerId,
      projectId: project.id,
      userId: sharee.userId ?? "",
      shareEvent: CHANGE_SHARING_EVENT.UserRoleChanged,
      content: role,
    });
    this.onSharingChanged.emit({ projectId: project.id });
  }

  canRemoveSelfFromAccountSharing(project: ProjectInfo): boolean {
    // `RevokeSharedLink`: `isSelf && customerData.IsEnterpriseAccount` takes
    // the account-sharing exit instead of deleting a link — and it is the
    // only exit a same-account member WITHOUT a link has.
    const user = this.m_auth.currentUser;
    return !isProjectOwner(project, user?.userId) && this.isCurrentUserEnterpriseAccount();
  }

  async revokeAccessAsync(project: ProjectInfo, row: SharedUserJoinedInfo): Promise<void> {
    if (row.isSelf && this.isCurrentUserEnterpriseAccount()) {
      await this.removeSelfFromAccountSharingAsync(project);
      return;
    }
    if (row.link) await this.revokeSharedLinkAsync(project, row.link);
  }

  /**
   * `ShareViewModel.RemoveSelfFromAccountSharing` — "if users leave a project
   * themselves, they should also be removed from the account-wide sharing":
   * a `NoAccess` link for the user's own email (which the cross-account merge
   * reads as "remove this project from my grid"), the timestamp bump, and an
   * `evict_user` at THEMSELVES so every tab they have open leaves too.
   */
  private async removeSelfFromAccountSharingAsync(project: ProjectInfo): Promise<void> {
    const user = this.m_auth.currentUser;
    const inviterCustomerId = user?.customerId ?? "";
    if (!user || !inviterCustomerId) return;
    const email = user.email.toLowerCase();
    await this.m_storage.createSharedLink(inviterCustomerId, {
      ID: generateSharedLinkId(project.id, email),
      LinkType: DEEPLINK_TYPE.SceneLink,
      CustomerID: inviterCustomerId,
      ProjectID: project.id,
      SceneID: "",
      AnnotationID: "",
      SharedWithEmail: email,
      ProjectAuthor: this.projectAuthorFor(project),
      UserRole: SHARED_USER_ROLE.NoAccess,
      State: SHARED_LINK_STATE.Default,
    });
    await this.bumpProjectModifiedAsync(project);
    this.sendEvent("evict_user", buildEvictUserPayload({ projectId: project.id, userId: user.userId }));
    this.onSharingChanged.emit({ projectId: project.id });
  }

  /**
   * `ShareViewModel.OnConfirmRevokeSharedLink`: resolve the sharee, delete the
   * row on ITS account, bump the project timestamp, then the live legs — an
   * `evict_user` only when the sharee has VISITED (i.e. may be inside the
   * project right now) AND is not falling back onto an account-wide grant,
   * and a `ProjectUnshared` to the sharee's account (plus the project's
   * account when they differ) so their grid drops the project.
   */
  async revokeSharedLinkAsync(project: ProjectInfo, link: SharedLinkDto): Promise<void> {
    const customerId = link.CustomerID || this.projectCustomerId(project);
    if (!customerId) return;
    const sharee = await this.getUserInfoAsync((link.SharedWithEmail ?? "").toLowerCase());
    await this.m_storage.deleteSharedLink(customerId, link.ID);
    await this.bumpProjectModifiedAsync(project);
    debugSharing("revoke: link row deleted", {
      id: link.ID,
      account: customerId,
      email: link.SharedWithEmail,
      visited: isSharedLinkVisited(link),
      evict: isSharedLinkVisited(link) && !this.isAccountSharingAvailable(project, sharee.accountId),
    });
    this.onSharingChanged.emit({ projectId: project.id });

    if (isSharedLinkVisited(link) && !this.isAccountSharingAvailable(project, sharee.accountId)) {
      this.sendEvent("evict_user", buildEvictUserPayload({ projectId: project.id, userId: sharee.userId ?? "" }));
    }
    this.sendChangeSharingToShareeAndProject(project, sharee.accountId, sharee.userId, CHANGE_SHARING_EVENT.ProjectUnshared, SHARED_USER_ROLE.Viewer);
  }

  async notifyProjectDeletedAsync(project: ProjectInfo, links: ReadonlyArray<SharedLinkDto>): Promise<void> {
    // `CustomerModel.DeleteProjectAsync`: one `ProjectUnshared` per external
    // link (Project/Scene links with a real, non-`internal` email — NOT the
    // roster filter, a `NoAccess` row counts), addressed to the sharee's
    // account. A sharee whose email no longer resolves is skipped, and the
    // deletion proceeds regardless.
    for (const link of links) {
      const type = link.LinkType ?? 0;
      const email = (link.SharedWithEmail ?? "").toLowerCase();
      if (type !== DEEPLINK_TYPE.ProjectLink && type !== DEEPLINK_TYPE.SceneLink) continue;
      if (!email || email === INTERNAL_SHARE_EMAIL) continue;
      try {
        const sharee = await this.m_admin.lookupUserByEmailAsync(email);
        this.sendChangeSharing({
          accountId: sharee.accountId ?? "",
          projectId: project.id,
          userId: sharee.userId ?? "",
          shareEvent: CHANGE_SHARING_EVENT.ProjectUnshared,
          content: SHARED_USER_ROLE.Viewer,
        });
      } catch {
        // "sharing email is not a valid user, continue with project deletion"
      }
    }
  }

  /**
   * `IsAccountSharingAvailable(shareeAccountId)` — the sharee is on the
   * project's own account AND that account still has a grant, so revoking
   * their individual link leaves them with access and they must NOT be
   * evicted.
   */
  private isAccountSharingAvailable(project: ProjectInfo, shareeAccountId: string | null): boolean {
    return this.projectCustomerId(project) === shareeAccountId
      && (project.accountRole ?? SHARED_USER_ROLE.Editor) !== SHARED_USER_ROLE.NoAccess;
  }

  /**
   * The two-address pattern every share / unshare uses upstream: one event to
   * the SHAREE's account, and — when the project lives elsewhere — a second
   * to the PROJECT's account so its members see the roster change.
   */
  private sendChangeSharingToShareeAndProject(
    project: ProjectInfo,
    shareeAccountId: string | null,
    shareeUserId: string | null,
    shareEvent: number,
    content: number,
  ): void {
    const projectCustomerId = this.projectCustomerId(project);
    this.sendChangeSharing({
      accountId: shareeAccountId ?? "",
      projectId: project.id,
      userId: shareeUserId ?? "",
      shareEvent,
      content,
    });
    if (shareeAccountId !== projectCustomerId) {
      this.sendChangeSharing({
        accountId: projectCustomerId,
        projectId: project.id,
        userId: shareeUserId ?? "",
        shareEvent,
        content,
      });
    }
  }

  private sendChangeSharing(args: Parameters<typeof buildChangeSharingPayload>[0]): void {
    this.sendEvent("change_sharing", buildChangeSharingPayload(args));
  }

  /**
   * Publish on the session socket stamped like `IProjectModel.
   * publishLiveProjectEvent` (`uid` / `aid` from `BaseEventData`) — but NOT
   * through it: that seam fills `projectId` from the OPEN project, and the
   * grid's share sheet has none. Both sharing events carry their own project
   * field (`projId` / `projectId`), so nothing is lost.
   */
  private sendEvent(type: string, payload: Record<string, unknown>): void {
    // MUST NOT THROW. The invite path publishes AFTER the row is written and
    // the email is sent, inside `InviteUser`'s try — a throw here would land
    // in the "user does not exist" catch and send the invite a second time.
    // A live event is a courtesy to peers; a failed one is a console line.
    try {
      this.m_events.send({
        type,
        payload: {
          ...payload,
          uid: this.m_auth.currentUser?.userId ?? null,
          aid: getAppInstanceId(),
        },
      });
    } catch (err) {
      console.warn(`[SharedUsersModel] publishing ${type} failed:`, err);
    }
  }

  async markProjectAsVisitedAsync(project: ProjectInfo): Promise<void> {
    const sharedLinkId = project.sharedLinkId;
    if (!sharedLinkId || !this.m_storage.isOnline) return;
    const customerId = this.projectCustomerId(project);
    if (!customerId) return;
    const link = await this.m_storage.getSharedLink(customerId, sharedLinkId);
    if (!link || isSharedLinkVisited(link)) return;
    await this.m_storage.updateSharedLinkState(
      customerId,
      sharedLinkId,
      sharedLinkState(link) | SHARED_LINK_STATE.Visited,
    );
  }

  // -- Invite internals ------------------------------------------------------

  /** `CheckMailValidity` — returns Unity's message, or `null` when valid. */
  private async checkMailValidityAsync(
    project: ProjectInfo,
    email: string,
    existingLinks: ReadonlyArray<SharedLinkDto>,
  ): Promise<string | null> {
    if (!isValidMailAddress(email)) return "Please enter a valid email address.";
    // `ContainsShareeWithEmail` reads the cached roster, i.e. the roster
    // filter — an `"internal"` or `NoAccess` row does not count.
    if (existingLinks.some((link) => isRosterShareLink(link) && link.SharedWithEmail === email)) {
      return "This email is already invited.";
    }
    const ownerEmail = this.m_ownerEmailByProjectId.get(project.id) ?? "";
    if (ownerEmail && email === ownerEmail) return "Cannot invite project owner.";
    if (email === (this.m_auth.currentUser?.email ?? "").toLowerCase()) return "Cannot invite yourself.";
    return null;
  }

  /** `InviteUser(string email)` — one address, Unity's order of guards. */
  private async inviteOneAsync(
    project: ProjectInfo,
    email: string,
    role: SharedUserRole,
  ): Promise<{ ok: boolean; message: string | null }> {
    try {
      const sharee = await this.getUserInfoAsync(email);
      debugSharing("invite: sharee resolved", {
        email,
        userId: sharee.userId,
        accountId: sharee.accountId,
        planType: sharee.planType,
        accountRole: sharee.accountRole,
        inviterPlan: this.m_customer.customerInfo?.planType ?? null,
        inviterPaid: this.m_customer.customerInfo?.isPaidAccount ?? null,
        role,
      });

      if (
        sharee.planType === CAMPFIRE_PLAN_TYPE.IndividualFree
        && role === SHARED_USER_ROLE.Editor
        && this.isCurrentUserEnterpriseAccount()
      ) {
        return { ok: false, message: 'This user is a Guest and cannot be an "Editor".' };
      }

      // "projects owned by users in my account will not have owner emails on
      // them, so must look up owner email now" — a THROW here (the one method
      // on the admin model that throws) falls into the catch below, which
      // still sends the invite. That is Unity's behaviour, not an oversight.
      if (!this.m_ownerEmailByProjectId.has(project.id) && project.creatorId) {
        const owner = await this.m_admin.getUserDataAsync(this.projectCustomerId(project), project.creatorId);
        this.m_ownerEmailByProjectId.set(project.id, owner.email.toLowerCase());
      }

      if (!(await this.canInviteUserAsync(sharee))) {
        // Verbatim `ShareViewModel.cs:474-485`, including which branch gets
        // which string.
        return {
          ok: false,
          message: this.canInviteExternalUsers()
            ? "You can only share projects with other users in your organization."
            : "You are not authorized to share with this user.",
        };
      }

      if (sharee.accountRole === ACCOUNT_ROLE.Reader && role === SHARED_USER_ROLE.Editor) {
        return { ok: false, message: 'This user is a Guest and cannot be an "Editor".' };
      }

      const sent = await this.sendSharedLinkAsync(project, email, role, sharee.userId);
      if (sent.ok) {
        // "inform user about new project share (account id from user)", then
        // "inform other users about new sharee (account id from project)".
        // Only on THIS path — the no-account `catch` below sends no event,
        // exactly as upstream.
        this.sendChangeSharingToShareeAndProject(project, sharee.accountId, sharee.userId, CHANGE_SHARING_EVENT.ProjectShared, role);
      }
      return sent;
    } catch (err) {
      // "user does not exist" — send the invite with no user id. `success`
      // stays true upstream.
      console.warn("[SharedUsersModel] invite lookup path threw; sending anyway:", err);
      return this.sendSharedLinkAsync(project, email, role, null);
    }
  }

  /**
   * `SharedUsersBaseModel.CanInviteUser` — the sharing rules, verbatim, with
   * the two web-only simplifications noted in the class header.
   */
  private async canInviteUserAsync(sharee: ShareeInfo): Promise<boolean> {
    // "always able to share with support"
    if (sharee.email === K_SUPPORT_EMAIL_ADDRESS) return true;

    const myCustomer = this.m_customer.customerInfo;
    if (!this.canInviteExternalUsers()) {
      return !!myCustomer && myCustomer.customerId === sharee.accountId;
    }

    if (myCustomer?.isPaidAccount) {
      // A managed user can invite anyone in a safe domain, or anyone at all
      // when the account has "share anyone" on…
      if (this.isDomainSafeListed(sharee.email) || myCustomer.shareAnyone) return true;
      // …and otherwise any member or explicitly-added guest of the account.
      return this.m_admin.emailShareTestAsync(myCustomer.customerId, sharee.email);
    }
    // A free user can always invite another free user, or a non-existent one…
    if (sharee.planType === CAMPFIRE_PLAN_TYPE.IndividualFree || sharee.accountId === null) return true;
    // …and a managed user only if they are a safe domain or explicit guest of
    // THAT account — asked with the literal email "NA", as upstream does.
    return this.m_admin.emailShareTestAsync(sharee.accountId, "NA");
  }

  /** `IsDomainSafeListed` — `ShareDomains` on, and the email ends with a safe domain. */
  private isDomainSafeListed(email: string): boolean {
    const customer = this.m_customer.customerInfo;
    if (!customer?.shareDomains) return false;
    const lower = email.toLowerCase();
    return customer.safeDomains.some((domain) => {
      const trimmed = domain.trim().toLowerCase();
      return trimmed.length > 0 && lower.endsWith(trimmed);
    });
  }

  private isCurrentUserEnterpriseAccount(): boolean {
    const plan = this.m_customer.customerInfo?.planType;
    return plan === CAMPFIRE_PLAN_TYPE.EnterpriseFree || plan === CAMPFIRE_PLAN_TYPE.EnterprisePaid;
  }

  /**
   * `SendSharedLink` + `CreateExternalSceneSharedLink` + `SendEmail`: write
   * the row, mail the link, toast, and let the roster reload.
   */
  private async sendSharedLinkAsync(
    project: ProjectInfo,
    email: string,
    role: SharedUserRole,
    shareeUserId: string | null,
  ): Promise<{ ok: boolean; message: string | null }> {
    const user = this.m_auth.currentUser;
    const inviterCustomerId = user?.customerId ?? "";
    if (!inviterCustomerId || !this.m_storage.isOnline) {
      return { ok: false, message: `Error sending invite to ${email}` };
    }
    // `ExternalSharedLink(project, email, role)` → `SceneLink`, no scene,
    // `State = Default`, the INVITER's account, the deterministic id.
    const link: SharedLinkDto = {
      ID: generateSharedLinkId(project.id, email),
      LinkType: DEEPLINK_TYPE.SceneLink,
      CustomerID: inviterCustomerId,
      ProjectID: project.id,
      SceneID: "",
      AnnotationID: "",
      SharedWithEmail: email,
      ProjectAuthor: this.projectAuthorFor(project),
      UserRole: role,
      State: SHARED_LINK_STATE.Default,
    };
    try {
      await this.m_storage.createSharedLink(inviterCustomerId, link);
      debugSharing("invite: link row written", { id: link.ID, account: inviterCustomerId, email });
    } catch (err) {
      console.warn("[SharedUsersModel] createSharedLink failed:", err);
      return { ok: false, message: `Error sending invite to ${email}` };
    }

    const sent = await this.m_admin.sendSharingEmailAsync({
      fromFirstName: user?.firstName || user?.displayName || "",
      inviteUrl: this.projectLinkUrl(project, link),
      inviteeUserId: shareeUserId,
      projectName: project.name,
      inviteeEmail: email,
    });
    debugSharing("invite: email lambda answered", { email, sent });
    if (!sent) {
      // Deviation: Unity toasts success without reading the lambda's answer.
      // The row exists either way (access IS granted), so this stays a
      // success for the roster and only the message says the mail failed.
      return { ok: true, message: `Error sending invite to ${email}` };
    }
    this.m_toasts.push({ variant: "success", message: `Project invite sent to ${email}` });
    return { ok: true, message: null };
  }

  /**
   * `ExternalSharedLink`'s `ProjectAuthor` rule (`DeepLinkGenerator.cs:93-103`):
   * the project's author, except a sample ("learn") project that has never
   * been shared, which is attributed to the inviter's name tag.
   */
  private projectAuthorFor(project: ProjectInfo): string {
    const isUserCreatedProject = project.category !== "learn";
    if (isUserCreatedProject || project.isShared) return project.ownerName ?? "";
    return this.m_auth.currentUser?.nameTag ?? "";
  }

  // -- Roster internals ------------------------------------------------------

  /**
   * `SharedUsersBaseModel.GetUserInfo(string email)` — `lookup` with the
   * cache rule above; a throw becomes the empty "no account" identity.
   */
  private async getUserInfoAsync(email: string, signal?: AbortSignal): Promise<ShareeInfo> {
    const cached = this.m_shareeInfoByEmail.get(email);
    if (cached) return cached;
    try {
      const info = await this.m_admin.lookupUserByEmailAsync(email, signal);
      if (info.userId !== null) this.m_shareeInfoByEmail.set(email, info);
      return info;
    } catch (err) {
      console.warn("[SharedUsersModel] lookup failed; treating as no account:", err);
      return emptyShareeInfo(email);
    }
  }

  /** `CreateJoinedInfo(SharedLinkData)`. */
  private async createJoinedInfoAsync(
    project: ProjectInfo,
    link: SharedLinkDto,
    canEdit: boolean,
    signal?: AbortSignal,
  ): Promise<SharedUserJoinedInfo> {
    const email = (link.SharedWithEmail ?? "").toLowerCase();
    const sharee = await this.getUserInfoAsync(email, signal);

    // "IsVisited-flag is not correct if user is already inside project when
    // the shared link is created" — a live participant counts as visited.
    const visited = isSharedLinkVisited(link) || this.isUserActive(project, sharee.userId);
    const isDeletedUser = visited && sharee.userId === null;

    // Display role: Presenter is retired, and the sharee's own account role
    // caps what the link can grant.
    let role: SharedUserRole = sharedLinkUserRole(link);
    if (sharee.accountRole === ACCOUNT_ROLE.Reader) {
      role = SHARED_USER_ROLE.Viewer;
    } else if (sharee.accountRole === ACCOUNT_ROLE.Commenter && role === SHARED_USER_ROLE.Editor) {
      role = SHARED_USER_ROLE.Commenter;
    }

    const myUserId = this.m_auth.currentUser?.userId ?? null;
    const isSelf = sharee.userId !== null && sharee.userId === myUserId;
    const canChangeRole = canEdit && !isDeletedUser;
    return {
      kind: "share",
      link,
      sharedLinkId: link.ID,
      email,
      firstName: sharee.firstName,
      lastName: sharee.lastName,
      userId: sharee.userId,
      accountId: sharee.accountId,
      planType: sharee.planType,
      accountRole: sharee.accountRole,
      // "project user role is updated faster than shared link on server"
      role: isSelf ? this.activeUserRoleFor(project) : role,
      isSelf,
      isDeletedUser,
      canRevoke: canEdit || isSelf,
      canChangeRole,
      availableRoles: this.availableRolesFor(sharee, canChangeRole, role),
      visited,
    };
  }

  /** `CreateOwnerJoinedInfo`. */
  private async createOwnerJoinedInfoAsync(
    project: ProjectInfo,
    signal?: AbortSignal,
  ): Promise<SharedUserJoinedInfo> {
    const user = this.m_auth.currentUser;
    if (isProjectOwner(project, user?.userId)) {
      return { ...this.createSelfJoinedInfo(project), kind: "owner", role: SHARED_USER_ROLE.Owner, canRevoke: false };
    }
    // Split the author into first / last the way upstream does; resolve the
    // owner's email through the profile query when we have a creator id.
    const author = project.ownerName ?? "";
    const space = author.indexOf(" ");
    const firstName = space > 0 ? author.slice(0, space) : author;
    const lastName = space > 0 ? author.slice(space + 1).split(" ")[0] : "";
    let email = this.m_ownerEmailByProjectId.get(project.id) ?? "";
    if (!email && project.creatorId) {
      try {
        const owner = await this.m_admin.getUserDataAsync(this.projectCustomerId(project), project.creatorId, signal);
        email = owner.email.toLowerCase();
        if (email) this.m_ownerEmailByProjectId.set(project.id, email);
      } catch {
        // Unity's owner row simply has no email until something resolves it.
      }
    }
    return {
      kind: "owner",
      link: null,
      sharedLinkId: null,
      email,
      firstName,
      lastName,
      userId: project.creatorId ?? null,
      accountId: this.projectCustomerId(project) || null,
      planType: this.m_customer.customerInfo?.planType ?? CAMPFIRE_PLAN_TYPE.Unknown,
      accountRole: ACCOUNT_ROLE.Writer,
      role: SHARED_USER_ROLE.Owner,
      isSelf: false,
      isDeletedUser: false,
      canRevoke: false,
      canChangeRole: false,
      availableRoles: [SHARED_USER_ROLE.Owner],
      visited: true,
    };
  }

  /** `CreateSelfJoinedInfo`. */
  private createSelfJoinedInfo(project: ProjectInfo): SharedUserJoinedInfo {
    const user = this.m_auth.currentUser;
    return {
      kind: "self",
      link: null,
      sharedLinkId: null,
      email: (user?.email ?? "").toLowerCase(),
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      userId: user?.userId ?? null,
      accountId: user?.customerId ?? null,
      planType: this.m_customer.customerInfo?.planType ?? CAMPFIRE_PLAN_TYPE.Unknown,
      accountRole: this.m_permissions.naturalAccountRole,
      role: this.activeUserRoleFor(project),
      isSelf: true,
      isDeletedUser: false,
      // Unity: `!IsCurrentUserProjectOwner()`. This row has no link to delete,
      // so on the web it is only actionable where Unity's "Leave" would do
      // something — the enterprise account-sharing exit.
      canRevoke: this.canRemoveSelfFromAccountSharing(project),
      canChangeRole: false,
      availableRoles: [this.activeUserRoleFor(project)],
      visited: true,
    };
  }

  /**
   * `SharedUsersListItemViewModel` constructor (`:190-224`): role options are
   * the first entries of the same dropdown that ends in Revoke/Leave.
   */
  private availableRolesFor(
    sharee: ShareeInfo,
    canChangeRole: boolean,
    currentRole: SharedUserRole,
  ): ReadonlyArray<SharedUserRole> {
    if (!canChangeRole) return [currentRole];
    const roles: SharedUserRole[] = [SHARED_USER_ROLE.Viewer];
    if (
      sharee.accountRole === ACCOUNT_ROLE.Commenter
      || sharee.accountRole === ACCOUNT_ROLE.Presenter
      || sharee.accountRole === ACCOUNT_ROLE.Writer
    ) {
      roles.push(SHARED_USER_ROLE.Commenter);
    }
    if (
      sharee.accountRole === ACCOUNT_ROLE.Writer
      && !(
        sharee.planType === CAMPFIRE_PLAN_TYPE.IndividualFree
        && this.isCurrentUserEnterpriseAccount()
      )
    ) {
      roles.push(SHARED_USER_ROLE.Editor);
    }
    return roles;
  }

  /** `IsUserActive` — is this user live in the project's session right now. */
  private isUserActive(project: ProjectInfo, userId: string | null): boolean {
    if (!userId) return false;
    const participants = this.m_multiuser.getSessionParticipants({
      projectId: project.id,
      accountId: this.projectCustomerId(project) || null,
    });
    return participants.some((p) => p.userId === userId && p.isOnline);
  }

  /**
   * Unity bumps the project row's `ModifiedTimestamp` after every invite
   * batch / role change / revoke purely to invalidate the desktop's
   * share-list cache — the web must keep doing it so desktop clients refresh.
   */
  private async bumpProjectModifiedAsync(project: ProjectInfo): Promise<void> {
    const customerId = this.projectCustomerId(project);
    if (!customerId) return;
    try {
      await this.m_storage.updateCustomerProjectModifiedDate(customerId, project.id);
    } catch (err) {
      console.warn("[SharedUsersModel] project timestamp bump failed:", err);
    }
  }
}

/** `AppConfig.SupportEmailAddress` — identical in dev and prod `app-config.json`. */
const K_SUPPORT_EMAIL_ADDRESS = "support@campfire3d.com";

/**
 * Dev-only breadcrumbs for the invite / revoke flow. Every refusal in this
 * model is a plain message on the share sheet with no stack behind it, so
 * when "nothing happened" the console is the only way to see which guard
 * answered — and these are the lines that say so.
 */
function debugSharing(step: string, detail: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return;
  console.debug(`[SharedUsersModel] ${step}`, detail);
}

/** The empty `ShareeInfoModel { Email = email }` Unity builds for a stranger. */
function emptyShareeInfo(email: string): ShareeInfo {
  return {
    planType: CAMPFIRE_PLAN_TYPE.Unknown,
    firstName: "",
    lastName: "",
    accountId: null,
    userId: null,
    email,
    // `default(AccountRole)` is 0 — not a member; equals neither Reader nor
    // Commenter, so the display-role clamps leave the link's role alone.
    accountRole: 0,
  };
}

/**
 * `System.Net.Mail.MailAddress` accepts more than a strict RFC pattern would;
 * what matters for parity is that a plain `local@domain.tld` passes and a
 * bare word does not. Kept as the shape the web already used.
 */
function isValidMailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function ensureTrailingSlash(value: string): string {
  return value.endsWith("/") ? value : `${value}/`;
}
